<script setup lang="ts">
import { computed, ref } from "vue";
import { ApiClientError } from "../api/client";
import { copyServiceSnapshot, createServiceSnapshotTemplate, getServiceSnapshotTemplate, listServiceSnapshotTemplates,
  type ServiceSnapshot, type ServiceSnapshotTemplate } from "../api/service-info-templates";
import { coreServiceFields } from "./service-info-defaults";

const props = defineProps<{ snapshot: ServiceSnapshot; disabled: boolean }>();
const emit = defineEmits<{ apply: [snapshot: ServiceSnapshot] }>();
const panel = ref<"save" | "apply" | null>(null);
const name = ref("");
const items = ref<ServiceSnapshotTemplate[]>([]);
const selectedId = ref("");
const selected = computed(() => items.value.find(item => item.id === selectedId.value));
const working = ref(false);
const notice = ref("");
const error = ref("");
// Keep the exact UUID and payload until the outcome has been read back.
const pending = ref<ServiceSnapshotTemplate | null>(null);
const absent = ref(false);
const preview = computed(() => {
  const snapshot = selected.value?.snapshot;
  if (!snapshot) return [];
  return [...coreServiceFields.map(field => ({ title: snapshot.config.standard[field.key].title || field.title,
    summary: snapshot.config.standard[field.key].summary, content: snapshot.values[field.key],
    visible: snapshot.config.standard[field.key].visible, sortOrder: snapshot.config.standard[field.key].sortOrder })),
  ...snapshot.config.custom].sort((a, b) => a.sortOrder - b.sortOrder);
});
async function openApply() {
  if (props.disabled || working.value) return;
  panel.value = "apply"; error.value = ""; notice.value = ""; working.value = true;
  try { items.value = await listServiceSnapshotTemplates(); selectedId.value = ""; }
  catch (cause) { error.value = cause instanceof Error ? cause.message : "模板读取失败，请重新打开重试"; }
  finally { working.value = false; }
}
function openSave() {
  if (props.disabled || working.value) return;
  panel.value = "save"; error.value = ""; notice.value = "";
}
function saved(template: ServiceSnapshotTemplate) {
  items.value = [template, ...items.value.filter(item => item.id !== template.id)];
  pending.value = null; absent.value = false; name.value = ""; panel.value = null;
  notice.value = `已保存模板“${template.name}”，其他商品可从模板应用。`; error.value = "";
}
async function submit() {
  if (props.disabled || working.value || (pending.value && !absent.value)) return;
  const templateName = name.value.normalize("NFKC").trim();
  if (!pending.value && (!templateName || templateName.length > 100)) { error.value = "请填写1至100字的模板名称"; return; }
  if (!pending.value && props.snapshot.config.custom.some(item => !item.title.trim())) { error.value = "请先填写自定义服务名称，或删除空白服务项"; return; }
  pending.value ??= { id: crypto.randomUUID(), name: templateName, snapshot: copyServiceSnapshot(props.snapshot) };
  working.value = true; error.value = ""; absent.value = false;
  try { saved(await createServiceSnapshotTemplate(pending.value)); }
  catch (cause) {
    error.value = cause instanceof Error ? cause.message : "模板保存失败";
    if (cause instanceof ApiClientError && [400, 403, 409, 422].includes(cause.status)) pending.value = null;
    else error.value += "。保存结果需要核对，请勿重复新建。";
  } finally { working.value = false; }
}
async function reconcile() {
  if (!pending.value || working.value || props.disabled) return;
  working.value = true; error.value = "";
  try {
    const actual = await getServiceSnapshotTemplate(pending.value.id);
    // Display the persisted immutable snapshot; never issue another POST during readback.
    if (actual.name !== pending.value.name || JSON.stringify(actual.snapshot) !== JSON.stringify(pending.value.snapshot)) {
      // JSON object ordering may differ in MySQL; the matching UUID is not enough to trust a conflicting payload.
      const canonical = (value: unknown): string => Array.isArray(value) ? `[${value.map(canonical).join(",")}]`
        : value && typeof value === "object" ? `{${Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(",")}}` : JSON.stringify(value);
      if (canonical(actual) !== canonical(pending.value)) { error.value = "该编号已保存的内容与本次不同，请保留当前内容并联系管理员核对"; return; }
    }
    saved(actual);
  } catch (cause) {
    if (cause instanceof ApiClientError && cause.status === 404) {
      absent.value = true; error.value = "尚未查到该模板。可以使用原编号和原内容再次保存，不会新增重复模板。";
    } else error.value = cause instanceof Error ? cause.message : "核对失败，请稍后再次核对";
  } finally { working.value = false; }
}
function applySelected() {
  if (!selected.value || props.disabled || working.value) return;
  emit("apply", copyServiceSnapshot(selected.value.snapshot, true));
  notice.value = `已将“${selected.value.name}”填入当前草稿，点击保存服务信息后生效。`;
  panel.value = null;
}
</script>

<template>
  <div class="template-tools">
    <div class="template-actions">
      <button
        type="button"
        :disabled="disabled || working"
        @click="openSave"
      >
        保存为模板
      </button>
      <button
        type="button"
        :disabled="disabled || working"
        @click="openApply"
      >
        从模板应用
      </button>
      <span>整套复制，各商品独立保存</span>
    </div>
    <section
      v-if="panel"
      class="template-panel"
      :aria-label="panel === 'save' ? '保存服务模板' : '应用服务模板'"
    >
      <template v-if="panel === 'save'">
        <label for="service-template-name">模板名称</label>
        <div class="template-form">
          <input
            id="service-template-name"
            v-model="name"
            maxlength="100"
            placeholder="例如：数码动漫通用服务"
            :disabled="disabled || working || !!pending"
            @keydown.enter.prevent="submit"
          >
          <button
            type="button"
            class="primary"
            :disabled="disabled || working || (!!pending && !absent)"
            @click="submit"
          >
            {{ working ? '保存中…' : absent ? '使用原内容再次保存' : '确认保存模板' }}
          </button>
        </div>
        <p>保存当前填写的勾选状态、名称、简短说明、完整详情和自定义项，包含尚未保存的服务修改。不会修改当前商品。</p>
        <button
          v-if="pending"
          type="button"
          :disabled="disabled || working"
          @click="reconcile"
        >
          核对模板保存结果
        </button>
      </template>
      <template v-else>
        <label for="service-template-select">选择已保存模板</label>
        <select
          id="service-template-select"
          v-model="selectedId"
          :disabled="disabled || working"
        >
          <option value="">
            {{ working ? '正在读取模板…' : '请选择模板' }}
          </option>
          <option
            v-for="item in items"
            :key="item.id"
            :value="item.id"
          >
            {{ item.name }}
          </option>
        </select>
        <p v-if="!working && !items.length && !error">
          还没有模板。先在商品中填好服务信息，再点击“保存为模板”。
        </p>
        <template v-if="selected">
          <ul class="template-preview">
            <li
              v-for="(row, index) in preview"
              :key="index"
            >
              <strong>{{ row.title }}</strong><span>{{ row.visible ? '展示' : '隐藏' }}</span>
              <p>{{ row.summary || '未填写简短说明' }}</p>
              <details><summary>查看完整详情</summary><p>{{ row.content || '未填写详情' }}</p></details>
            </li>
          </ul>
          <p class="replace-note">
            将替换当前草稿的四项服务及全部自定义项，包括未保存的修改。应用后仍可编辑，点击“保存服务信息”后才生效。
          </p>
          <button
            type="button"
            class="primary"
            :disabled="disabled || working"
            @click="applySelected"
          >
            确认替换当前草稿
          </button>
        </template>
      </template>
      <button
        type="button"
        class="close-panel"
        :disabled="working"
        @click="panel = null"
      >
        收起
      </button>
    </section>
    <p
      v-if="error"
      role="alert"
    >
      {{ error }}
    </p>
    <p
      v-if="notice"
      role="status"
    >
      {{ notice }}
    </p>
  </div>
</template>

<style scoped>
.template-tools { margin: 0 0 16px; color: #171717; }
.template-actions, .template-form { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.template-actions > span, p { color: #666; font-size: 12px; line-height: 1.6; }
button, input, select { box-sizing: border-box; border: 1px solid #d4d4d4; border-radius: 4px; background: #fff; color: #171717; font: inherit; padding: 8px 12px; min-width: 0; }
button { cursor: pointer; }
button:hover:not(:disabled) { background: #f5f5f5; }
button:disabled, input:disabled, select:disabled { opacity: .5; cursor: default; }
button.primary { background: #171717; border-color: #171717; color: #fff; }
button.primary:hover:not(:disabled) { background: #333; }
.template-panel { margin-top: 12px; padding: 16px; border: 1px solid #e5e5e5; background: #fafafa; }
label { display: block; margin-bottom: 8px; font-weight: 500; }
input { flex: 1 1 220px; }
select { width: 100%; }
input::placeholder { color: #737373; }
.close-panel { margin-left: 8px; }
.template-preview { list-style: none; padding: 0; margin: 12px 0; }
.template-preview li { padding: 12px 0; border-bottom: 1px solid #e5e5e5; overflow-wrap: anywhere; }
.template-preview li > span { margin-left: 12px; color: #666; font-size: 12px; }
.template-preview p { margin: 6px 0; white-space: pre-wrap; }
summary { cursor: pointer; font-size: 12px; }
.replace-note, [role=alert] { color: #171717; }
:is(button, input, select, summary):focus-visible { outline: 2px solid #171717; outline-offset: 2px; }
@media (max-width: 640px) { .template-panel { padding: 12px; } .template-form > button { width: 100%; } .template-actions > span { flex-basis: 100%; } }
</style>
